import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { useFormikContext, useField } from 'formik';
import { Directions } from '../ecardsPreact.constants/directions';
import { PreviewState } from '../ecardsPreact.constants/previewState';
import { ArrowIcon } from '../ecardsPreact.icons/ArrowIcon';
import { HeartIcon } from '../ecardsPreact.icons/HeartIcon';
import { RemoveIcon } from '../ecardsPreact.icons/RemoveIcon';
import { ECardGlobalState } from './ECardGlobalState';
import requestManager from '../ecardsPreact.client/requestManager';

export default function ECardPreview(props) {
    const { Message, DonationAmount, DonationVisible, GtmDonationOption, EcardID, ImageUploadId, ImageUploadUrl, ImageToUpload } = props; // From EcardMessageDetails
    const { ecardDesignUrl, ecardDesignAlt, ecardDesignAspectRatio, previewState, onPreviewStateUpdate, recipient, envelope } = props;
    const { setSubmitting } = useFormikContext();
    const [imageToUploadField, imageToUploadMeta, imageToUploadHelpers] = useField('EcardMessageDetails.ImageToUpload');
    const [imageUploadUrlField, imageUploadUrlMeta, imageUploadUrlHelpers] = useField('EcardMessageDetails.ImageUploadUrl');
    const [imageUploadIdField, imageUploadIdMeta, imageUploadIdHelpers] = useField('EcardMessageDetails.ImageUploadId');
    const { isGtmCard } = useContext(ECardGlobalState);

    const mediaWidth = 600;
    const mediaHeight = 600 * (ecardDesignAspectRatio ?? 1); // An aspect ratio value isn't provided if the ecardDesign is a video. Assuming video upload will always be 1:1 ratio.

    const recipientText = recipient !== null && recipient.Name !== '' ? `To ${recipient.Name}` : `To [Recipients name]`;
    const messageText = Message && Message.length > 0 ? Message : `[Your Message will appear here]`;

    const imageUploadUrl = ImageToUpload !== null ? URL.createObjectURL(ImageToUpload) : ImageUploadUrl ? ImageUploadUrl : '';

    // check if ImageUploadUrl is jpg, png or jpeg
    const imageExtension = imageUploadUrl.split('.').pop().toLowerCase();
    const isResizableUploadedImage = imageExtension === 'jpg' || imageExtension === 'png' || imageExtension === 'jpeg';

    const pips = Object.values(PreviewState).map((state) => {
        if (!isGtmCard && state === PreviewState.Gtm) {
            return null;
        }

        return (
            <span
                key={state}
                class={`preview-controls__pip${state === previewState ? ' preview-controls__pip--active' : ''}`}
                onClick={() => {
                    onPreviewStateUpdate(state);
                }}
            ></span>
        );
    });

    const video = useRef(null);
    const [controlState, setControlState] = useState(0); // 0 = stopped, 1 = playing, 2 = paused

    const handlePrevClick = () => {
        let newState = PreviewState.Front;

        if (isGtmCard) {
            const optionsArr = Object.values(PreviewState);
            const currentIndex = optionsArr.indexOf(previewState);
            const newIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
            newState = PreviewState[optionsArr[newIndex]];
        }

        onPreviewStateUpdate(newState);
    };

    const handleNextClick = () => {
        let newState = PreviewState.Back;

        if (isGtmCard) {
            const optionsArr = Object.values(PreviewState);
            const currentIndex = optionsArr.indexOf(previewState);
            const newIndex = currentIndex >= optionsArr.length - 1 ? optionsArr.length - 1 : currentIndex + 1;
            newState = PreviewState[optionsArr[newIndex]];
        }

        onPreviewStateUpdate(newState);
    };

    const handlePlayClick = () => {
        video.current.play();
        video.current.muted = false;
        onPreviewStateUpdate(PreviewState.Front);
    };

    const handlePauseClick = () => {
        video.current.pause();
    };

    const handleReplayClick = () => {
        onPreviewStateUpdate(PreviewState.Front);
        setControlState(null);
        setCanFlip(false);
        envelope.current.close(false, true).then(() => {
            hasFlipped.current = false;
            setControlState(0);
            envelope.current.open().then(() => {
                delayAutoFlip();
            });
        });
    };

    const handleVideoPlay = () => {
        setControlState(1);
    };

    const handleVideoPause = () => {
        if ((envelope.current && video.current.currentTime === video.current.duration) || video.current.currentTime === 0) {
            setControlState(0);
        } else {
            setControlState(2);
        }
    };

    const handleRemoveImage = async () => {
        setSubmitting(true);
        if (ImageUploadId) {
            grecaptcha.ready(async () => {
                var token = await grecaptcha.execute('6LeZGbMaAAAAANO9dtVMb0UUOiczJYagWKVeb8y6', { action: 'submit' });

                const body = {
                    RecaptchaToken: token,
                    EcardId: EcardID,
                    ImageId: ImageUploadId,
                };

                const response = await requestManager.DoRequest('DELETE', '/api/EcardCreationApi/RemoveImage/', undefined, body);

                if (response.Success === true) {
                    imageToUploadHelpers.setValue(null);
                    imageUploadUrlHelpers.setValue(null);
                    imageUploadIdHelpers.setValue(null);
                }
            });
        } else if (ImageToUpload) {
            imageToUploadHelpers.setValue(null);
            imageUploadUrlHelpers.setValue(null);
        }
        setSubmitting(false);
    };

    const ecardDesignType = ecardDesignUrl.split('.').pop();

    const autoFlip = () => {
        if (!hasFlipped.current) {
            if (isGtmCard) {
                onPreviewStateUpdate(PreviewState.Gtm);
            } else {
                onPreviewStateUpdate(PreviewState.Back);
            }
        }
    };

    const delayAutoFlip = () => {
        setCanFlip(true);
        if (video.current) {
            video.current.addEventListener(
                'ended',
                (event) => {
                    autoFlip();
                },
                { once: true }
            );
        } else {
            setTimeout(() => {
                autoFlip();
            }, 3000);
        }
    };

    /**
     * START - The following code handles automatically flipping the card after 3 seconds on load, as well as cancelling it when the card is manually flipped.
     */
    const [canFlip, setCanFlip] = useState(false);
    const hasFlipped = useRef(false);
    const initialRender = useRef(true);

    useEffect(() => {
        if (initialRender.current) {
            if (envelope.current) {
                envelope.current.open(1000).then(() => {
                    delayAutoFlip();
                });
            } else {
                delayAutoFlip();
            }
            initialRender.current = false;
        } else {
            hasFlipped.current = true;
        }

        return () => {};
    }, [previewState]);

    /**
     * END
     */

    return (
        <div class={`ecard-preview${isGtmCard ? ' ecard-preview--gtm' : ''}`}>
            <div class={`ecard-preview__container ecard-preview__container--${(canFlip ? previewState : PreviewState.Front).toLowerCase()}`}>
                <div class="ecard-preview__side ecard-preview__side--front">
                    {ecardDesignType === 'mp4' || ecardDesignType === 'webp' || ecardDesignType === 'flv' ? (
                        <video
                            ref={video}
                            class="ecard-preview__media"
                            width={mediaWidth}
                            height={mediaHeight}
                            playsInline
                            muted
                            preload="auto"
                            onPlay={handleVideoPlay}
                            onPause={handleVideoPause}
                        >
                            <source src={`${ecardDesignUrl}#t=0.001`} type={`video/${ecardDesignType}`} />
                        </video>
                    ) : (
                        <img class="ecard-preview__media" width={mediaWidth} height={mediaHeight} src={ecardDesignUrl} alt={ecardDesignAlt} />
                    )}
                </div>

                {GtmDonationOption && (
                    <div class="ecard-preview__side ecard-preview__side--gtm bg-white p-15">
                        <img
                            src="/Areas/Ecards/Content/Images/paper-texture@2x.jpg?width=600&height=600"
                            srcset="/Areas/Ecards/Content/Images/paper-texture@2x.jpg?width=600&height=600 600w, /Areas/Ecards/Content/Images/paper-texture@2x.jpg 1000w"
                            sizes="(min-width: 1417px) 600px, (min-width: 1024px) calc(50vw - 100px), (min-width: 700px) 600px, calc(100vw - 100px)"
                            alt=""
                            class="ecard-preview__back-image"
                            loading="lazy"
                        />
                        {GtmDonationOption.LongDescription && (
                            <div className="rich-text rich-text--white" dangerouslySetInnerHTML={{ __html: GtmDonationOption.LongDescription }}></div>
                        )}
                    </div>
                )}

                <div class="ecard-preview__side ecard-preview__side--back">
                    <img
                        src="/Areas/Ecards/Content/Images/paper-texture@2x.jpg?width=600&height=600"
                        srcset="/Areas/Ecards/Content/Images/paper-texture@2x.jpg?width=600&height=600 600w, /Areas/Ecards/Content/Images/paper-texture@2x.jpg 1000w"
                        sizes="(min-width: 1417px) 600px, (min-width: 1024px) calc(50vw - 100px), (min-width: 700px) 600px, calc(100vw - 100px)"
                        alt=""
                        class="ecard-preview__back-image"
                        loading="lazy"
                    />
                    <div class="ecard-preview__back-container">
                        <h2 class="ecard-preview__recipient">{recipientText}</h2>
                        <p class="ecard-preview__message">{messageText}</p>
                        <span></span>

                        {(imageUploadUrl || (DonationVisible === true && DonationAmount > 0)) && (
                            <div class="ecard-preview__footer">
                                {imageUploadUrl && (
                                    <div class="ecard-preview__uploaded-image-wrapper">
                                        {isResizableUploadedImage ? (
                                            <img
                                                class="ecard-preview__uploaded-image"
                                                src={`${imageUploadUrl}?width=100`}
                                                srcSet={`${imageUploadUrl}?width=100 1x, ${imageUploadUrl}?width=200 2x, ${imageUploadUrl}?width=300 3x, ${imageUploadUrl}?width=400 4x`}
                                                alt=""
                                            />
                                        ) : (
                                            <img class="ecard-preview__uploaded-image" src={imageUploadUrl} alt="" />
                                        )}
                                        <button
                                            class="button-reset remove-button ecard-preview__uploaded-image-remove"
                                            type="button"
                                            onClick={async () => {
                                                await handleRemoveImage();
                                            }}
                                        >
                                            <span class="sr-only">Remove uploaded image</span>
                                            <RemoveIcon />
                                        </button>
                                    </div>
                                )}

                                {DonationVisible === true && DonationAmount > 0 && (
                                    <div class="ecard-preview__donation-wrapper">
                                        <HeartIcon className="ecard-preview__donation-icon text-blue" />
                                        <p class="ecard-preview__donation">
                                            Your card includes a <strong>£{DonationAmount.toFixed(2)} donation</strong> to support Marie Curie
                                        </p>
                                        <HeartIcon className="ecard-preview__donation-icon text-blue" />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {GtmDonationOption && (
                <p className="ecard-preview__info-footer">
                    <strong>
                        <span className="text-blue">&pound;{GtmDonationOption.Amount}</span> {GtmDonationOption.ShortDescriptionPrefix}:
                        <span className="text-blue"> {GtmDonationOption.ShortDescription}</span>
                    </strong>
                </p>
            )}
            <div class="preview-controls">
                {(ecardDesignType === 'mp4' || ecardDesignType === 'webp' || ecardDesignType === 'flv' || envelope.current) && (
                    <div class={`preview-controls__video`}>
                        <button
                            type="button"
                            onClick={handlePlayClick}
                            class="preview-controls__video-play"
                            disabled={envelope.current ? controlState !== 2 : controlState !== 2 && controlState !== 0}
                        >
                            Play
                        </button>
                        <button type="button" onClick={handlePauseClick} class="preview-controls__video-pause" disabled={controlState !== 1}>
                            Pause
                        </button>
                        <button
                            type="button"
                            onClick={handleReplayClick}
                            class="preview-controls__video-replay"
                            disabled={!envelope.current || controlState !== 0}
                        >
                            Replay
                        </button>
                    </div>
                )}
                <button
                    type="button"
                    onClick={handlePrevClick}
                    class="preview-controls__button preview-controls__button--prev"
                    disabled={previewState === PreviewState.Front}
                >
                    <ArrowIcon direction={Directions.Left} />
                    <span class="sr-only">View front</span>
                </button>
                <span class="preview-controls__pips">{pips}</span>
                <button
                    type="button"
                    onClick={handleNextClick}
                    class="preview-controls__button preview-controls__button--next"
                    disabled={previewState === PreviewState.Back}
                >
                    <ArrowIcon direction={Directions.Right} />
                    <span class="sr-only">View back</span>
                </button>
            </div>
        </div>
    );
}
